import { getIn } from "components/Formik/forms";
import { compact, isEqual, max, omit, pick, sortBy, uniqWith } from "lodash-es";
import { groupBy, isPresent, stripHTML } from "lib/utils";
import { CA_PROVINCES, STATES } from "lib/states";
import { ClientCampaignRecord, DegreeProgramRecord, ExclusionRuleRecord, FieldRecord, ISO8601DateTime } from "lib/DataModel";
import moment from "moment";
import MemorySource from "@orbit/memory";
import { GetRemoteIdFn } from "providers/OrbitProvider";
import { getTimezoneName } from "components/TimezoneSelector";
import { TreeDataGridRow } from "../common/TreeDataGrid";

type Option = {
  text: string
  value: string
}

type FormPostingRow = TreeDataGridRow & {
  createdAt?: ISO8601DateTime
  updatedAt?: ISO8601DateTime
}

type RequestParameterTopLevelRow = FormPostingRow & {
  name: string
  required?: boolean
  description?: string
}

type RequestParameterChildRow = FormPostingRow & {
  value: string
  text: string
}

type RequestParameterRow = RequestParameterTopLevelRow | RequestParameterChildRow

export type TopLevelProgramExclusionRow = FormPostingRow & {
  programName: string
  programCode: string
}

export type ExclusionRuleRow = FormPostingRow & {
  fieldName: string
  operator: string
  value: string
  time?: string
}

export type ProgramExclusionRow = TopLevelProgramExclusionRow | ExclusionRuleRow

export function isTopLevelProgramExclusionRow(row: TreeDataGridRow): row is TopLevelProgramExclusionRow {
  return 'programName' in row
}

const DEFAULT_DESCRIPTION = {
  first_name:     'First name',
  last_name:      'Last name',
  street_address: 'Street address',
  city:           'City',
  state:          'State',
  zip_code:       'ZIP code',
  email:          'Email',
  phone:          'Phone number (10 digits)',
}

const SOURCE_OPTIONS = [
  { value: 'facebook', text: 'Facebook Ads' } as const,
  { value: 'snapchat', text: 'Snapchat Ads' } as const,
  { value: 'google', text: 'Google Ads' } as const,
  { value: 'bing', text: 'Bing Ads / Microsoft Ads' } as const,
  { value: 'tiktok', text: 'TikTok Ads' } as const,
  { value: 'linkedin', text: 'LinkedIn Ads' } as const,
  { value: 'email', text: 'Email marketing campaigns' } as const,
  { value: 'owned', text: 'Affiliate-owned and operated sites' } as const,
  { value: 'unknown', text: 'Source cannot be determined' } as const,
] as const

const TEST_OPTIONS = [
  {value: 'true', text: 'Test lead'},
  {value: 'false', text: 'Real lead (default)'}
]

function fieldRank(field: FieldRecord) {
  const RANKINGS = [
  'first_name',
  'last_name',
  'phone',
  'email',
  'street_address',
  'city',
  'state',
  'zip_code',
  ]

  const index = RANKINGS.indexOf(getIn(field, 'attributes.fieldType'))
  return index < 0 ? RANKINGS.length : index
}

export function isWithin7Days(datetime: ISO8601DateTime) {
  if (!datetime) return false

  const sevenDaysAgo = moment().subtract(7, 'days')

  return moment(datetime).isSameOrAfter(sevenDaysAgo)
}

function findMostRecent<DateProperty extends string>(data: Partial<Record<DateProperty, ISO8601DateTime | undefined>>[], dateProperty: DateProperty) {
  return max(data.filter(record => record[dateProperty] !== undefined).map(record => record[dateProperty]))
}

function getFieldOptions(field: FieldRecord): Readonly<Option[]> {
  const fieldType = getIn(field, 'attributes.fieldType')
  if (fieldType === 'state') {
    return [...STATES, ...CA_PROVINCES].map(stateDef => ({ text: stateDef.name, value: stateDef.abbreviation }))
  }
  else if(fieldType === 'source') {
    return SOURCE_OPTIONS
  }
  else {
    return getIn(field, 'attributes.options') || []
  }
}

function getRowsFromFields(fields: FieldRecord[], getRemoteId: GetRemoteIdFn) {
  const result: RequestParameterRow[] = []
  fields.forEach((field: FieldRecord) => {
    const fieldId = getRemoteId(field) || field.id

    result.push({
      id: fieldId,
      name: getIn(field, 'attributes.name'),
      path: [fieldId],
      required: field.attributes.required,
      description: field.attributes.description ?? (field.attributes.fieldType ? DEFAULT_DESCRIPTION[field.attributes.fieldType] : undefined),
      updatedAt: field.attributes.updatedAt,
    })

    const fieldOptions = getFieldOptions(field)
    result.push(...getChildRows<RequestParameterChildRow>(fieldOptions, [fieldId]))
  })

  return result
}

// filter out items missing a value before constructing child rows
function getChildRows<R extends FormPostingRow>(options: Readonly<Omit<R, 'id' | 'path'>[]>, parentPath: string[]) : R[] {
  return options
    .filter(option => isPresent(option.value))
    .map((option, index) => {
      return {
        ...option,
        id: `${parentPath.join('-')}-${index}`,
        path: [...parentPath, index],
      } as R
    }
  ) || []
}

function getExclusionRuleRows(parentPath: string[], rule: ExclusionRuleRecord, store: MemorySource, getRemoteId: GetRemoteIdFn, clientTimezone: string): ExclusionRuleRow[] {
  const field = store.cache.query<FieldRecord>(q => q.findRelatedRecord(rule, 'field'))

  const fieldName = getIn(field, 'attributes.name')
  const ruleValues = getIn(rule, 'attributes.value') || []

  const lookup = Object.fromEntries(getFieldOptions(field).map((option: Option) => [option.value, option.text]))

  const ruleId = getRemoteId(rule) || rule.id
  const ruleOperator = getIn(rule, 'attributes.operator')

  return ruleValues.map((value: string, index: number) => {
    const text = lookup[value] ?? value

    return {
      id: [...parentPath, fieldName, `${ruleId}-${index}`].join('-'),
      path: [...parentPath, fieldName, `${ruleId}-${index}`],
      fieldName: fieldName,
      operator: ruleOperator,
      value: value,
      text: text,
      time: getTimeOfEffect(rule, clientTimezone),
      updatedAt: rule.attributes.updatedAt,
    }
  }) || []
}

export function getRequestParameterRows({ clientCampaigns, fields, degreePrograms, parameterNames, getRemoteId }: { clientCampaigns: ClientCampaignRecord[], fields: FieldRecord[], degreePrograms: DegreeProgramRecord[], parameterNames: Record<string,string>, getRemoteId: GetRemoteIdFn }): RequestParameterRow[] {
  const campaignIdOptions = sortBy(clientCampaigns, 'attributes.name').map(clientCampaign => {
    return {
      value: getIn(clientCampaign, 'attributes.publicId'),
      text: getIn(clientCampaign, 'attributes.name'),
      createdAt: getIn(clientCampaign, 'attributes.createdAt'),
    }
  })

  // vendors don't get access to program status and only get back programs in active status (client users see status)
  const filteredDegreePrograms = degreePrograms.filter(program => getIn(program, 'attributes.status') != 'inactive' && getIn(program, 'attributes.status') != 'hidden')
  const programCodeOptions = sortBy(filteredDegreePrograms, 'attributes.name').map(degreeProgram => {
    return {
      value: getIn(degreeProgram, 'attributes.programCode'),
      text: stripHTML(getIn(degreeProgram, 'attributes.name')),
      createdAt: getIn(degreeProgram, 'attributes.createdAt'),
    }
  })
  const sortedFields = sortBy(fields, field => fieldRank(field) + (getIn(field, 'attributes.required') ? 100 : 200))

  const getParameterName = (key: string) => parameterNames[key] || key

  return [
    { id: 'campaign_id', path: ['campaign_id'], name: getParameterName('campaign_id'), required: true, description: 'Campaign ID', createdAt: findMostRecent(campaignIdOptions, 'createdAt') },
    ...getChildRows<RequestParameterChildRow>(campaignIdOptions, ['campaign_id']),
    { id: 'program_code', path: ['program_code'], name: getParameterName('program_code'), required: true, description: 'Program Code', createdAt: findMostRecent(programCodeOptions, 'createdAt') },
    ...getChildRows<RequestParameterChildRow>(programCodeOptions, ['program_code']),
    ...getRowsFromFields(sortedFields, getRemoteId),
    { id: 'test', path: ['test'], name: getParameterName('test'), required: false, description: 'Set to indicate a test lead' },
    ...getChildRows<RequestParameterChildRow>(TEST_OPTIONS, ['test'])
  ]
}

function getTimeOfEffect(rule: ExclusionRuleRecord, timezone: string): string | undefined {
  const dayOfWeek = getIn(rule, 'attributes.dayOfWeek')
  const startTimeOfDay = getIn(rule, 'attributes.startTimeOfDay')
  const endTimeOfDay = getIn(rule, 'attributes.endTimeOfDay')

  const isTimeBasedRule = !!(dayOfWeek || startTimeOfDay || endTimeOfDay)
  const isDayOnlyRule = dayOfWeek && !startTimeOfDay && !endTimeOfDay

  if (isDayOnlyRule) {
    return dayOfWeek
  }
  else if (isTimeBasedRule) {
    const dayOfWeekFormatted = dayOfWeek ? moment().day(dayOfWeek).format('ddd') : undefined
    const startTimeFormatted = startTimeOfDay ? moment.utc(startTimeOfDay).format('h:mm A') : '12:00 AM'
    const endTimeFormatted = endTimeOfDay ? moment.utc(endTimeOfDay).format('h:mm A') : '11:59 PM'

    return compact([dayOfWeekFormatted, `${startTimeFormatted} - ${endTimeFormatted}`, getTimezoneName(timezone)]).join(', ')
  } else {
    return undefined
  }
}

function deduplicateExclusionRuleRows(exclusionRuleRows: ExclusionRuleRow[]): ExclusionRuleRow[] {
  return uniqWith(exclusionRuleRows, (a, b) => isEqual(omit(a, 'id', 'path'), omit(b, 'id', 'path')))
}

const CLIENT_EXCLUSION_RULE_GROUP_KEY = ['fieldName', 'operator', 'time'] as const

export function getClientExclusionRows({ store, exclusionRules, getRemoteId, clientTimezone }: { store: MemorySource, exclusionRules: ExclusionRuleRecord[], getRemoteId: GetRemoteIdFn, clientTimezone: string }): ExclusionRuleRow[] {
  const exclusionRuleRows = deduplicateExclusionRuleRows(exclusionRules.flatMap(rule => getExclusionRuleRows([], rule, store, getRemoteId, clientTimezone)))

  return groupBy(exclusionRuleRows, row => pick(row, ...CLIENT_EXCLUSION_RULE_GROUP_KEY), { comparator: isEqual, asEntries: true }).flatMap(([groupKey, groupedRows]: [Partial<ExclusionRuleRow>, ExclusionRuleRow[]]) => {
    const parentId = Object.values(groupKey).join('-')
    if(groupedRows.length > 1) {
      const maxUpdatedAt = max(compact(groupedRows.map(row => row.updatedAt)))

      return [
        {
          ...groupKey,
          id: parentId,
          path: [parentId],
          updatedAt: maxUpdatedAt,
        },
        ...groupedRows.map(row => ({
          ...omit(row, ...CLIENT_EXCLUSION_RULE_GROUP_KEY, 'updatedAt'),
          id: `${parentId}-${row.id}`,
          path: [parentId, ...row.path],
        })),
      ]
    }
    else {
      return {
        ...groupedRows[0],
        id: parentId,
        path: [parentId],
      }
    }
  })
}

export function getProgramExclusionRows({ store, degreePrograms, getRemoteId, clientTimezone}: {store: MemorySource, degreePrograms: DegreeProgramRecord[], getRemoteId: GetRemoteIdFn, clientTimezone: string }) {
  const result: ProgramExclusionRow[] = []

  degreePrograms
    // vendors don't get access to program status and only get back programs in active status (client users see status)
    .filter(program => getIn(program, 'attributes.status') != 'inactive' && getIn(program, 'attributes.status') != 'hidden')
    .forEach(program => {
      const programExclusionRules = store.cache.query<ExclusionRuleRecord[]>(q => q.findRelatedRecords(program, 'exclusionRules'))
      const programId = getRemoteId(program) || program.id

      const exclusionRuleRows: ExclusionRuleRow[] = deduplicateExclusionRuleRows(programExclusionRules.flatMap(rule => getExclusionRuleRows([programId], rule, store, getRemoteId, clientTimezone)))

      if (exclusionRuleRows.length > 0) {
        const programRow = {
          id: programId,
          path: [programId],
          programName: getIn(program, 'attributes.name'),
          programCode: getIn(program, 'attributes.programCode'),
          updatedAt: max(compact(exclusionRuleRows.map(row => row.updatedAt))),
        }

        result.push(programRow, ...exclusionRuleRows.map((row: ExclusionRuleRow) => omit(row, 'updatedAt') as ExclusionRuleRow)) // FIXME: possible eslint types bug that requires this explicit cast
      }
    })

  return result
}
